import { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, CircularProgress, TextField,
  useTheme, Box, Typography,
  // Imports for mobile responsiveness
  useMediaQuery,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Checkbox
} from '@mui/material';
import { saveAs } from 'file-saver';
import { useSelector } from 'react-redux';
import { State } from '../../state/reducers';
import dayjs from 'dayjs';
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult
} from 'react-beautiful-dnd';
import { getVehiclesFromDb } from '../../api/getVehiclesFromDb';
import { vehicleCsvFields } from './vehicleCsvFields';
import { useSession } from '../../context/SessionContext';

interface GenerateCsvModalProps {
  open: boolean;
  onClose: () => void;
}


const initialFieldList = vehicleCsvFields.map(({ key, label, enabled }) => ({ key, label, enabled }));
const baseUrl = window.location.origin;

const GenerateCsvModal: React.FC<GenerateCsvModalProps> = ({ open, onClose }) => {
  const [generating, setGenerating] = useState(false);
  const [enabledFields, setEnabledFields] = useState(initialFieldList.filter(f => f.enabled));
  const [disabledFields, setDisabledFields] = useState(initialFieldList.filter(f => !f.enabled));
  const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [currentPage, setCurrentPage] = useState<number | null>(null);
  const [totalPages, setTotalPages] = useState<number | null>(null);

  const { selectedSystem } = useSession();
  const systemCode = selectedSystem?.systemCode || ''
  const theme = useTheme();
  // Detect if we are on a small screen
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Handler for desktop drag-and-drop
  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    const sourceList = source.droppableId === 'enabled' ? [...enabledFields] : [...disabledFields];
    const destList = destination.droppableId === 'enabled' ? [...enabledFields] : [...disabledFields];

    const [movedItem] = sourceList.splice(source.index, 1);

    if (source.droppableId === destination.droppableId) {
      sourceList.splice(destination.index, 0, movedItem);
      if (source.droppableId === 'enabled') setEnabledFields(sourceList);
      else setDisabledFields(sourceList);
    } else {
      destList.splice(destination.index, 0, movedItem);
      if (source.droppableId === 'enabled') {
        setEnabledFields(sourceList);
        setDisabledFields(destList.sort((a, b) => a.label.localeCompare(b.label)));
      } else {
        setDisabledFields(sourceList);
        setEnabledFields(destList);
      }
    }
  };

  // Handler for mobile tap-to-toggle
  const handleToggleField = (fieldKey: string) => {
    const isCurrentlyEnabled = enabledFields.some(f => f.key === fieldKey);

    if (isCurrentlyEnabled) {
      // Move from enabled to disabled
      const fieldToMove = enabledFields.find(f => f.key === fieldKey);
      if (fieldToMove) {
        setEnabledFields(enabledFields.filter(f => f.key !== fieldKey));
        setDisabledFields([...disabledFields, fieldToMove].sort((a, b) => a.label.localeCompare(b.label)));
      }
    } else {
      // Move from disabled to enabled
      const fieldToMove = disabledFields.find(f => f.key === fieldKey);
      if (fieldToMove) {
        setDisabledFields(disabledFields.filter(f => f.key !== fieldKey));
        setEnabledFields([...enabledFields, fieldToMove]); // Append to the end
      }
    }
  };

  const generateCSV = async () => {
    setGenerating(true);
    try {
      // ... existing CSV generation logic (no changes needed)
      const perPage = 100;
      let totalVehicles = 0;
      let allVehicles: any[] = [];
      setCurrentPage(1);
      const firstResponse = await getVehiclesFromDb({
        page: 1, per_page: perPage, system_code: systemCode,
        start_date: startDate, end_date: endDate,
      });
      allVehicles = firstResponse.vehicles;
      totalVehicles = firstResponse.total;
      const total = Math.ceil(totalVehicles / perPage);
      setTotalPages(total);
      for (let page = 2; page <= total; page++) {
        setCurrentPage(page);
        const response = await getVehiclesFromDb({
          page, per_page: perPage, system_code: systemCode,
          start_date: startDate, end_date: endDate,
        });
        allVehicles.push(...response.vehicles);
      }
      const csvHeaders = enabledFields.map(f => f.label);
      const csvRows = allVehicles.map(v =>
        enabledFields.map(field => {
          const extractor = vehicleCsvFields.find(f => f.key === field.key)?.extractor;
          return extractor ? extractor(v) : '';
        })
      );
      const csvContent = [csvHeaders, ...csvRows].map(row => row.join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      saveAs(blob, `vehicles_export_${Date.now()}.csv`);
    } catch (err) {
      console.error('CSV generation error:', err);
      alert("Error generating CSV.");
    } finally {
      setGenerating(false);
      onClose();
    }
  };


  const csvUrl = `${baseUrl}/csv-download?client=${selectedSystem}&start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}&fields=${enabledFields.map(f => f.key).join(',')}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>Generate CSV</DialogTitle>
      <DialogContent>
        {generating ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="300px"
          >
            <CircularProgress />
            <Typography variant="h6" mt={3}>Generating CSV...</Typography>
            {currentPage && totalPages && (
              <Typography variant="body2" mt={1}>
                Downloading page {currentPage} of {totalPages}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />

            {isMobile ? (
              // --- MOBILE VIEW ---
              <Box mt={2}>
                <Typography sx={{ fontWeight: 600, mb: 1 }}>Select Fields:</Typography>
                <List sx={{ maxHeight: 300, overflow: 'auto', border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                  {enabledFields.map((field) => (
                    <ListItem key={field.key} disablePadding>
                      <ListItemButton onClick={() => handleToggleField(field.key)}>
                        <ListItemIcon><Checkbox edge="start" checked={true} tabIndex={-1} /></ListItemIcon>
                        <ListItemText primary={field.label} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                  {disabledFields.map((field) => (
                    <ListItem key={field.key} disablePadding>
                      <ListItemButton onClick={() => handleToggleField(field.key)}>
                        <ListItemIcon><Checkbox edge="start" checked={false} tabIndex={-1} /></ListItemIcon>
                        <ListItemText primary={field.label} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : (
              // --- DESKTOP VIEW ---
              <DragDropContext onDragEnd={handleDragEnd}>
                <div style={{ display: 'flex', gap: 24, marginTop: 16 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: 8, fontWeight: 600 }}>Enabled Fields (Drag to reorder):</div>
                    <Droppable droppableId="enabled">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} style={{ maxHeight: 360, overflowY: 'auto', padding: 12, border: `1px solid ${theme.palette.divider}`, borderRadius: 8, backgroundColor: theme.palette.background.default }}>
                          {enabledFields.map((field, index) => (
                            <Draggable key={field.key} draggableId={field.key} index={index}>
                              {(provided) => (
                                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} style={{ padding: 6, margin: '8px 0', border: `1px solid ${theme.palette.divider}`, borderRadius: 4, fontSize: '14px', backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, ...provided.draggableProps.style }}>
                                  {field.label}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ marginBottom: 8, fontWeight: 600 }}>Disabled Fields:</div>
                    <Droppable droppableId="disabled">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} style={{ maxHeight: 360, overflowY: 'auto', padding: 12, border: `1px solid ${theme.palette.divider}`, borderRadius: 8, backgroundColor: theme.palette.background.default }}>
                          {disabledFields.map((field, index) => (
                            <Draggable key={field.key} draggableId={field.key} index={index}>
                              {(provided) => (
                                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} style={{ padding: 6, margin: '8px 0', border: `1px solid ${theme.palette.divider}`, borderRadius: 4, fontSize: '14px', backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, ...provided.draggableProps.style }}>
                                  {field.label}
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                </div>
              </DragDropContext>
            )}

            <Box sx={{ mt: 3, p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, backgroundColor: theme.palette.background.default }}>
              <Box sx={{ fontWeight: 600, mb: 1 }}>Direct CSV Download Link:</Box>
              <Box sx={{ fontSize: '0.8rem', wordBreak: 'break-word', color: theme.palette.text.secondary, mb: 1 }}>
                {csvUrl}
              </Box>
              <Button size="small" variant="outlined" onClick={async () => { try { await navigator.clipboard.writeText(csvUrl); alert("CSV link copied to clipboard!"); } catch (err) { console.error("Failed to copy link:", err); alert("Failed to copy link. Try again."); } }}>
                Copy Link
              </Button>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={generating} sx={{ color: theme.palette.error.main, border: `1px solid ${theme.palette.error.main}`, textTransform: 'none', '&:hover': { backgroundColor: theme.palette.error.light, borderColor: theme.palette.error.dark } }}>
          Cancel
        </Button>
        <Button onClick={generateCSV} disabled={generating} sx={{ color: theme.palette.warning.contrastText, backgroundColor: theme.palette.warning.main, textTransform: 'none', '&:hover': { backgroundColor: theme.palette.warning.dark } }}>
          {generating ? <CircularProgress size={20} sx={{ color: theme.palette.warning.contrastText }} /> : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GenerateCsvModal;